import * as React from "react"
import { Button, Col, Form, Modal } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import { APIError } from "../../../api/ObservableFromFetch"
import { SetMissingPeriods, MissedPeriodsData } from "../../../api/Randoms/PoolGeneralInfoGet"
import PoolData from "../../../api/Randoms/RandomsModel/PoolData"
import FormatError from "../../../formatters/FormatError"
import ActionButton from "../../ActionButton"

interface MissedPeriodsModalProps {
  details: PoolData
  onHide: () => void
  onSave: () => void
}

interface MissedPeriodsModalState {
  saving: boolean
  MakeUp: boolean
  TotalCompletedDrug?: number
  TotalCompletedAlcohol?: number
  AveragePoolSize?: number
}

export default class MissedPeriodsModal extends React.PureComponent<MissedPeriodsModalProps, MissedPeriodsModalState> {
  state: MissedPeriodsModalState = {
    saving: false,
    MakeUp: true,
    TotalCompletedDrug: undefined,
    TotalCompletedAlcohol: undefined,
    AveragePoolSize: undefined
  }

  saveSubscription?: Subscription

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => this.setState({ MakeUp: e.target.value == "makeup", TotalCompletedDrug: undefined, TotalCompletedAlcohol: undefined, AveragePoolSize: undefined })

  onSave = () => {
    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    const { MakeUp, TotalCompletedDrug, TotalCompletedAlcohol, AveragePoolSize } = this.state

    const data: MissedPeriodsData = {
      PoolProgramID: this.props.details.PoolProgramID,
      MakeUp: MakeUp ? 1 : 0,
      TotalCompletedDrug: MakeUp ? 0 : TotalCompletedDrug || 0,
      TotalCompletedAlcohol: MakeUp ? 0 : TotalCompletedAlcohol || 0,
      AveragePoolSize: MakeUp ? 0 : AveragePoolSize || 0
    }

    this.saveSubscription = SetMissingPeriods(data).subscribe(
      data => {
        toast.success(data)
        this.props.onSave()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  changeInput = (value: string, fieldName: keyof MissedPeriodsModalState) => {
    const enteredValue = value.replace(/[^0-9]+/g, '')
    if (/^(0|[1-9]{1}\d{0,18})$/.test(enteredValue)) {
      this.setState({...{}, [fieldName]: Number(enteredValue) })
    }
    else {
      this.setState({...{}, [fieldName]: undefined })
    }
  }

  className = (disable: boolean) => disable ? "disabled" : ""
  inputValue = (value: number | string | undefined) => value || value?.toString() === "0" ? value.toString() : ""

  validForm = (): boolean => {
    const { MakeUp, TotalCompletedDrug, TotalCompletedAlcohol, AveragePoolSize } = this.state
    const validSizes = (TotalCompletedDrug !== undefined && TotalCompletedAlcohol !== undefined && AveragePoolSize !== undefined)
    const valid = MakeUp ? true : validSizes
    return valid
  }

  render() {
    const { onHide } = this.props
    const { MakeUp, TotalCompletedDrug, TotalCompletedAlcohol, AveragePoolSize, saving } = this.state

    return (
      <Modal backdrop="static" show={true} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Missing Periods</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Row className="p-2 font-weight-bold">
              If one or more periods have been missed for this pool group. Please choose one of the following options.
            </Form.Row>
            <Form.Check label="Allow Workforce to make up required tests for missed periods."
              id="makeup-radio"
              type="radio"
              name="selectionType"
              className="abc-radio mt-3"
              value={"makeup"}
              checked={MakeUp}
              onChange={this.handleSelect} />
            <Form.Check label="Enter total number of tests already performed for the missed periods for this pool group."
              id="enter-radio"
              type="radio"
              name="selectionType"
              className="abc-radio mb-3"
              value={"enter"}
              checked={!MakeUp}
              onChange={this.handleSelect} />

            <Form.Row className={`form-group required ${this.className(MakeUp)}`}>
              <Form.Label column sm={6} htmlFor="totalDrug">Total # of drug tests completed</Form.Label>
              <Col sm={6}>
                <Form.Control id="totalDrug" type="text" value={this.inputValue(TotalCompletedDrug)} disabled={MakeUp} maxLength={10} onChange={(e) => this.changeInput(e.target.value, "TotalCompletedDrug")} />
              </Col>
            </Form.Row>
            <Form.Row className={`form-group required ${this.className(MakeUp)}`}>
              <Form.Label column sm={6} htmlFor="totalAlcohol">Total # of alcohol tests completed</Form.Label>
              <Col sm={6}>
                <Form.Control id="totalAlcohol" type="text" value={this.inputValue(TotalCompletedAlcohol)} disabled={MakeUp} maxLength={10} onChange={(e) => this.changeInput(e.target.value, "TotalCompletedAlcohol")} />
              </Col>
            </Form.Row>
            <Form.Row className={`form-group required ${this.className(MakeUp)}`}>
              <Form.Label column sm={6} htmlFor="average">Average pool size during the missed periods</Form.Label>
              <Col sm={6}>
                <Form.Control id="average" type="text" value={this.inputValue(AveragePoolSize)} disabled={MakeUp} maxLength={10} onChange={(e) => this.changeInput(e.target.value, "AveragePoolSize")} />
              </Col>
            </Form.Row>

            <Form.Row className="p-2">
              When choosing this option the total number of tests and average pool size should be entered prior to running the first random selection. These values are necessary for RedArrow to calculate the proper number of selections and provide accurate statistics. If the actual numbers are unknown, then estimated values should be entered when creating the pool. The pool detail form provides a link ("Total tests performed for missed periods") so that these values can be updated later.
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} className="mr-auto fixed-w5">CANCEL</Button>
          <ActionButton variant="primary" onClick={this.onSave} disabled={!this.validForm()} spin={saving} disableOnSpin={true} className="fixed-w5">SAVE</ActionButton>
        </Modal.Footer>
      </Modal>
    )
  }
}